import type { IsoDate, Lead, Signal } from '../../../../shared/types';
import { shell } from '../../app';
import { getSnapshot, onStoreChange } from '../../data/store';
import { todayLocal } from '../../logic/dates';
import { afterRemoval, stepSelection } from '../../logic/desk';
import { activeFilters } from '../../logic/signalFilters';
import { sectionsOf } from '../../logic/signalRank';
import { SOURCE_NAMES } from '../../logic/signals';
import { btn, empty, segmented } from '../../ui/components';
import type { Modal } from '../../ui/dialog';
import { h, replaceChildren, span } from '../../ui/dom';
import type { Child } from '../../ui/dom';
import { kindIcon } from '../../ui/icons';
import { isTypingEvent } from '../../ui/keys';
import { BUCKET_SECTION_TITLES, NEAR_SECTION_TITLE, SIGNALS_EMPTY_HINT } from '../../ui/labels';
import { placeMap } from '../../ui/placeMap';
import type { View } from '../../ui/router';
import { createInbox } from '../signals/inbox';
import { openNotFitSheet } from '../signals/notFitSheet';
import { signalCard } from '../signals/signalCard';
import { placeOf, rowMeta, signalDetail } from './signalDetail';
import { signalFilterBar } from './signalFilterBar';
import { deskSources } from './signalsSources';

/** New businesses on the desktop (D65, D74): the sorted list on the left — worth a visit first, the rest
 *  below, the hidden on their own under Show — and the business in full on the right, as in Mail. The
 *  list sits under the trust line and its filters (D66); with none selected the right side is the
 *  sources sheet, and the trust line brings it back. ↑/↓ move; A adds and plans, S adds, D dismisses,
 *  N opens the … sheet; after a triage the next one opens. */

type Show = 'top' | 'all' | 'hidden';
const SHOW_OPTIONS = [
  { value: 'top', label: 'Worth a visit' },
  { value: 'all', label: 'All' },
  { value: 'hidden', label: 'Hidden' },
] as const;
const KEY_ACTIONS = { a: 'addAndPlan', s: 'add', d: 'dismiss' } as const;
const DEFAULT_STATUS = 'Loading source status…';

interface ListSection {
  title: string;
  rows: Signal[];
}

export const signalsDeskView: View = ({ root, query }) => {
  shell.setChrome(true);
  let selected: string | null = query.get('id');
  let current: Signal | null = null;
  let show: Show = 'all';
  let sheet: Modal | null = null;
  let reveal = selected !== null; // a business a link names scrolls into view once its row exists
  const inbox = createInbox(() => paint(), { keepEmptyKinds: true });
  // the list pane's parts are made once: the scroll container keeps its place through every repaint
  const statusEl = h(
    'button',
    { type: 'button', class: 'split__status', title: 'The sources', onClick: () => select(null) },
    DEFAULT_STATUS,
  );
  const showEl = h(
    'div',
    { class: 'split__show' },
    segmented<Show>({
      options: SHOW_OPTIONS,
      value: show,
      size: 'md',
      onChange: (v) => {
        show = v;
        reveal = true;
        paint();
      },
    }),
  );
  const filterBar = signalFilterBar(inbox, () => paint());
  const listHead = h('div', { class: 'split__head' }, statusEl, showEl, filterBar.el);
  const offlineNote = h('p', { class: 'sig-offline', hidden: true });
  const rowsEl = h('div', { class: 'split__rows', role: 'listbox', 'aria-label': 'New businesses' });
  const routeHost = h('div');
  const listPane = h('div', { class: 'split__list' }, listHead, offlineNote, rowsEl, routeHost);
  const detailPane = h('div', { class: 'split__detail' });
  const sources = deskSources({ refresh: () => void inbox.load(), imported: () => void inbox.load() });
  // the business on the map, and the rep's leads near it (D80): the leads from the mirror, placed as
  // the phone already placed them
  const map = placeMap();
  let leads: Lead[] = [];
  const refreshLeads = () =>
    void getSnapshot().then((snap) => {
      leads = snap.leads;
      paint();
    });
  root.appendChild(h('div', { class: 'page page--signals-desk split' }, listPane, detailPane));
  shell.setTitle('New businesses');

  /** The list as Show has it: the sections with rows, in the server's order. */
  const sectionsShown = (): ListSection[] => {
    const { top, near, more, hidden } = inbox.sections();
    const of = (title: string, rows: Signal[]): ListSection[] => (rows.length ? [{ title, rows }] : []);
    if (show === 'hidden') return of(BUCKET_SECTION_TITLES.hidden, hidden);
    return [
      ...of(BUCKET_SECTION_TITLES.top, top),
      ...(show === 'all' ? [...of(NEAR_SECTION_TITLE, near), ...of(BUCKET_SECTION_TITLES.queue, more)] : []),
    ];
  };
  const ids = () => sectionsShown().flatMap((sec) => sec.rows.map((s) => s.id));
  const select = (id: string | null) => {
    selected = id;
    reveal = true;
    paint();
  };
  /** A triage moves the selection on to the next business before the row goes. */
  const moveOn = (s: Signal) => {
    selected = afterRemoval(ids(), s.id);
    reveal = true;
  };
  const triageNow = (s: Signal, act: (s: Signal) => Promise<void>) => {
    moveOn(s);
    void act(s);
  };
  const triage = (ev: MouseEvent, s: Signal, act: (s: Signal) => Promise<void>) => {
    // the second click of a double-click: the next business is painted under the pointer, and its
    // button must not take it — a deliberate second click, however quick, is its own (detail 1)
    if (ev.detail > 1) return;
    triageNow(s, act);
  };
  const openSheet = (s: Signal) => (sheet = openNotFitSheet(inbox, s, sheet, () => moveOn(s)));
  const pick = (id: string, on: boolean) => {
    if (on) inbox.picked.add(id);
    else inbox.picked.delete(id);
    paint();
  };

  const sigRow = (s: Signal, today: IsoDate): HTMLElement =>
    h(
      'div',
      {
        class: ['sigrow', s.id === selected ? 'sigrow--selected' : ''].filter(Boolean).join(' '),
        role: 'option',
        'aria-selected': String(s.id === selected),
        onClick: () => select(s.id),
      },
      h('input', {
        type: 'checkbox',
        class: 'sigrow__pick',
        checked: inbox.picked.has(s.id),
        'aria-label': `Pick ${s.business_name} for a route`,
        onClick: (e: MouseEvent) => e.stopPropagation(),
        onChange: (e: Event) => pick(s.id, (e.currentTarget as HTMLInputElement).checked),
      }),
      kindIcon(s.kind),
      h(
        'span',
        { class: 'sigrow__text' },
        span('sigrow__name', s.business_name),
        span('sigrow__meta', rowMeta(s, today, inbox.state.anchor)),
      ),
      s.bucket === 'hidden' ? null : span('sigrow__score mono', String(s.score)),
      span('srctag', SOURCE_NAMES[s.source]),
    );

  const filtered = () => activeFilters(inbox.filters).length > 0;
  /** Whether the list is in: loaded, and not a failed first load with nothing stored. */
  const listIn = () => inbox.state.loaded && !(inbox.state.error && inbox.all.length === 0);
  /** "238 new · Checked 2h ago · 14 sources", or "12 of 238 · …" while filtered; the trust line alone
   *  until the list is in. */
  const status = (shown: number): string => {
    const trust = inbox.state.sources?.trust.text ?? (inbox.state.offlineNote || inbox.state.error ? 'Source status needs a connection' : DEFAULT_STATUS);
    if (!listIn()) return trust;
    if (!filtered()) return `${inbox.summary.total} new · ${trust}`;
    const total =
      show === 'hidden'
        ? inbox.hidden.length
        : show === 'top'
          ? sectionsOf({ signals: inbox.all, hidden: [] }).top.length
          : inbox.all.length;
    return `${shown} of ${total} loaded · ${trust}`;
  };
  const nothing = (): HTMLElement => {
    if (filtered()) return empty('No business matches these filters.', 'Untick one above, or Clear them.');
    if (show === 'hidden')
      return empty('Nothing hidden.', 'Chains, duplicates and businesses outside the county land here.');
    const more = inbox.visible().length;
    if (show === 'top' && more)
      return empty('Nothing worth a visit today.', `${more} more to review under All.`);
    return empty("You're caught up.", SIGNALS_EMPTY_HINT);
  };
  const rows = (sections: ListSection[]): Child => {
    const { error, loaded } = inbox.state;
    if (!loaded) return h('p', { class: 'split__note' }, 'Loading…');
    if (error && inbox.all.length === 0 && inbox.hidden.length === 0) return empty(error.text, error.hint);
    if (sections.length === 0) return nothing();
    const today = todayLocal();
    return sections.map((sec) => [
      h('div', { class: 'split__day' }, sec.title),
      sec.rows.map((s) => sigRow(s, today)),
    ]);
  };

  const routeBar = (): HTMLElement | null => {
    const { planning } = inbox.state;
    if (inbox.picked.size === 0 && !planning) return null;
    const button = btn(planning ? 'Planning…' : `Route ${inbox.picked.size} picked`, {
      variant: 'primary',
      class: 'w-full',
      onClick: () => void inbox.routePicks(),
    });
    button.disabled = planning;
    return h('div', { class: 'split__routebar' }, button);
  };

  const card = (s: Signal): HTMLElement =>
    signalCard(s, {
      picked: inbox.picked.has(s.id),
      onPick: (on) => pick(s.id, on),
      onAddPlan: (ev) => triage(ev, s, inbox.addAndPlan),
      onAdd: (ev) => triage(ev, s, inbox.add),
      onDismiss: (ev) => triage(ev, s, inbox.dismiss),
      onShow: (ev) => triage(ev, s, inbox.show),
      onNotAChain: (ev) => triage(ev, s, inbox.notAChain),
      onMore: () => openSheet(s),
      anchor: inbox.state.anchor,
    });

  const detail = (s: Signal): Child => signalDetail(s, { card: card(s), mapEl: map.el, leads });

  const paint = () => {
    const sections = sectionsShown();
    const shown = sections.flatMap((sec) => sec.rows);
    current = shown.find((s) => s.id === selected) ?? null;
    statusEl.textContent = status(shown.length);
    filterBar.update();
    offlineNote.hidden = !inbox.state.offlineNote;
    offlineNote.textContent = inbox.state.offlineNote ?? '';
    const top = rowsEl.scrollTop;
    replaceChildren(rowsEl, inbox.summary.partial ? h('p', { class: 'split__note' }, inbox.summary.partial) : null, rows(sections));
    rowsEl.scrollTop = top;
    const selectedRow = rowsEl.querySelector('.sigrow--selected');
    if (reveal && selectedRow) {
      selectedRow.scrollIntoView?.({ block: 'nearest' });
      reveal = false;
    }
    replaceChildren(routeHost, routeBar());
    if (!current)
      sources.update(inbox.state.sources?.rows ?? null, inbox.state.updatedAt, inbox.state.sources?.google);
    replaceChildren(detailPane, current ? detail(current) : sources.el);
    const at = current ? placeOf(current) : null;
    if (at && current) void map.show(at, current.business_name);
    else map.hide();
  };

  // ↑/↓ move the list and A, S, D, N act on the open business — not while typing, inside a filter menu
  // or while a dialog is open
  const onKey = (e: KeyboardEvent) => {
    if (isTypingEvent(e) || e.metaKey || e.ctrlKey || e.altKey) return;
    if ((e.target as Element | null)?.closest?.('.fbar, dialog') || document.querySelector('dialog[open]'))
      return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      select(stepSelection(ids(), selected, e.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (!current) return;
    const key = e.key.toLowerCase();
    if (current.bucket === 'hidden' && key !== 'd') return; // a hidden card offers Dismiss alone
    if (key === 'n') {
      e.preventDefault();
      openSheet(current);
      return;
    }
    if (!(key in KEY_ACTIONS)) return;
    e.preventDefault();
    triageNow(current, inbox[KEY_ACTIONS[key as keyof typeof KEY_ACTIONS]]);
  };
  document.addEventListener('keydown', onKey);
  paint();
  void inbox.load();
  refreshLeads();
  const offStore = onStoreChange(refreshLeads);
  return () => {
    inbox.dispose();
    filterBar.dispose();
    sources.dispose();
    sheet?.remove();
    offStore();
    map.dispose();
    document.removeEventListener('keydown', onKey);
  };
};
